/* ==========================================================================
   Robolta 2026 — behaviour for rb-* sections.
   Each component is a custom element, so it re-initialises automatically when
   the theme editor re-renders a section.
   ========================================================================== */
(() => {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  // Videos never download during page load: sources are attached only after window 'load'
  let pageLoaded = document.readyState === 'complete';
  if (!pageLoaded) window.addEventListener('load', () => { pageLoaded = true; }, { once: true });
  // Mark videos once they really play (hero fades its video in over the image)
  document.addEventListener('playing', (e) => { if (e.target.tagName === 'VIDEO') e.target.classList.add('is-ready'); }, true);
  const whenLoaded = (fn) => (pageLoaded ? fn() : window.addEventListener('load', () => setTimeout(fn, 300), { once: true }));
  // Phones/tablets with native HLS get Shopify's adaptive stream (data-src-hls); others the MP4
  const nativeHls = !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
  const smallScreen = window.matchMedia('(max-width: 1024px)');
  const srcFor = (v) => (nativeHls && smallScreen.matches && v.dataset.srcHls) || v.dataset.src;

  /* ---------- Announcement bar: vertical rotator ---------- */
  class RbAnnounce extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.rb-announce__track');
      this.count = this.track ? this.track.children.length : 0;
      if (this.count < 2) return;

      this.track.appendChild(this.track.firstElementChild.cloneNode(true)); // seamless loop
      this.index = 0;
      this.speed = (parseFloat(this.dataset.speed) || 4) * 1000;
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.start();
    }

    disconnectedCallback() { this.stop(); }

    start() { this.stop(); this.timer = setInterval(() => this.step(), this.speed); }
    stop() { clearInterval(this.timer); }

    step() {
      this.index += 1;
      this.track.style.transition = 'transform .6s cubic-bezier(.65,0,.35,1)';
      this.track.style.transform = `translateY(-${this.index * 100 / (this.count + 1)}%)`;
      if (this.index === this.count) {
        setTimeout(() => {
          this.track.style.transition = 'none';
          this.track.style.transform = 'translateY(0)';
          this.index = 0;
        }, 650);
      }
    }
  }
  define('rb-announce', RbAnnounce);

  /* ---------- Header: sticky height, touch mega menu, mobile drawer ---------- */
  class RbHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.rb-header__bar');
      this.drawer = this.querySelector('[data-rb-drawer]');
      this.openers = this.querySelectorAll('[data-rb-drawer-open]');
      this.ac = new AbortController();
      const signal = this.ac.signal;

      // Other theme parts (sticky product gallery, drawers) read --header-height
      if (this.bar && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          const h = this.dataset.sticky === 'true' ? this.bar.offsetHeight : 0;
          document.documentElement.style.setProperty('--header-height', `${h}px`);
          this.fit();
        });
        this.ro.observe(this.bar);
      }
      this.fit();
      document.fonts && document.fonts.ready.then(() => this.fit());

      this.openers.forEach((b) => b.addEventListener('click', () => this.toggle(true), { signal }));
      this.querySelectorAll('[data-rb-drawer-close]').forEach((b) => b.addEventListener('click', () => this.toggle(false), { signal }));
      document.addEventListener('keyup', (e) => { if (e.key === 'Escape') { this.toggle(false); this.closeMega(); } }, { signal });

      // Touch screens: first tap on a parent link opens its panel, second tap follows the link
      const touch = window.matchMedia('(hover: none)');
      this.querySelectorAll('.rb-nav__item[data-has-panel] > .rb-nav__link').forEach((link) => {
        link.addEventListener('click', (e) => {
          const item = link.parentElement;
          if (!touch.matches || item.classList.contains('is-open')) return;
          e.preventDefault();
          this.closeMega();
          item.classList.add('is-open');
        }, { signal });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeMega(); }, { signal });

      // Close the drawer if the viewport grows to desktop
      window.matchMedia('(min-width: 1024px)').addEventListener('change', (e) => { if (e.matches) this.toggle(false); }, { signal });
    }

    disconnectedCallback() {
      this.ac && this.ac.abort();
      this.ro && this.ro.disconnect();
      document.documentElement.classList.remove('rb-lock');
    }

    // Long menus: if the links don't fit beside the logo and icons, use the burger drawer instead
    fit() {
      const nav = this.querySelector('.rb-nav');
      if (!nav) return;
      this.classList.remove('is-compact');
      if (getComputedStyle(nav).display === 'none') return;
      // Compare the last link's edge, not scrollWidth (hidden dropdown panels would count)
      const last = nav.lastElementChild;
      this.classList.toggle('is-compact', !!last && last.getBoundingClientRect().right > nav.getBoundingClientRect().right + 1);
    }

    closeMega() { this.querySelectorAll('.rb-nav__item.is-open').forEach((i) => i.classList.remove('is-open')); }

    toggle(open) {
      if (!this.drawer) return;
      this.drawer.classList.toggle('is-open', open);
      this.drawer.setAttribute('aria-hidden', String(!open));
      this.openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
      document.documentElement.classList.toggle('rb-lock', open);
      if (open) {
        const close = this.drawer.querySelector('[data-rb-drawer-close]:not(.rb-drawer__overlay)');
        close && close.focus();
      }
    }
  }
  define('rb-header', RbHeader);

  /* ---------- Hero carousel ---------- */
  class RbHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.rb-slide')];
      this.dots = [...this.querySelectorAll('[data-rb-dot]')];
      if (!this.slides.length) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = (parseFloat(this.dataset.speed) || 7) * 1000;
      this.index = 0;
      this.ac = new AbortController();
      const signal = this.ac.signal;

      const prev = this.querySelector('[data-rb-prev]');
      const next = this.querySelector('[data-rb-next]');
      prev && prev.addEventListener('click', () => this.go(this.index - 1), { signal });
      next && next.addEventListener('click', () => this.go(this.index + 1), { signal });
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i), { signal }));

      // Swipe
      let x0 = null, y0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true, signal });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      }, { signal });

      // Pause when the tab is hidden or the hero is off-screen
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([en]) => { this.offscreen = !en.isIntersecting; this.schedule(); this.syncVideos(); });
        this.io.observe(this);
      }

      // Theme editor: jump to the selected slide and hold it
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.indexOf(e.target);
        if (i < 0) return;
        this.held = true;
        this.go(i);
      }, { signal });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.slides.includes(e.target)) { this.held = false; this.schedule(); }
      }, { signal });

      this.go(0);
      whenLoaded(() => this.syncVideos());
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.ac && this.ac.abort();
      this.io && this.io.disconnect();
    }

    get paused() { return !this.autoplay || this.held || this.offscreen || document.hidden || this.slides.length < 2; }

    go(i) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((s, j) => {
        const on = j === this.index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', String(!on));
        s.toggleAttribute('inert', !on);
      });
      this.dots.forEach((d, j) => {
        d.classList.remove('is-active');
        d.removeAttribute('aria-current');
        if (j === this.index) {
          void d.offsetWidth; // restart progress animation
          d.classList.add('is-active');
          d.setAttribute('aria-current', 'true');
        }
      });
      this.classList.toggle('is-light-ui', this.slides[this.index].dataset.ui === 'light');
      this.syncVideos();
      this.schedule();
    }

    // Phones show the slide image only, unless the section allows mobile video
    get videoAllowed() { return this.dataset.mobileVideo === 'true' || !window.matchMedia('(max-width: 700px)').matches; }

    syncVideos() {
      this.slides.forEach((s, j) => s.querySelectorAll('video').forEach((v) => {
        if (j === this.index && !this.offscreen && (this.videoAllowed || !v.classList.contains('rb-video--over'))) {
          if (!v.getAttribute('src') && v.dataset.src) {
            if (!pageLoaded) return; // attached by whenLoaded() after the page finishes loading
            v.src = srcFor(v);
          }
          const p = v.play();
          p && p.catch(() => {});
        } else {
          v.pause();
        }
      }));
    }

    schedule() {
      clearTimeout(this.timer);
      this.classList.toggle('is-paused', this.paused);
      if (this.paused) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.speed);
    }
  }
  define('rb-hero', RbHero);

  /* ---------- Scroll reveal + count-up numbers ---------- */
  const countUp = (el) => {
    const end = parseFloat(el.dataset.rbCount);
    if (isNaN(end)) return;
    const dec = parseInt(el.dataset.rbDec, 10) || 0;
    const t0 = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / 1400);
      const v = end * (1 - Math.pow(1 - k, 3));
      el.textContent = dec ? v.toFixed(dec) : Math.round(v).toLocaleString('en-IN');
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const revealIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => entries.forEach((en) => {
    if (!en.isIntersecting) return;
    en.target.classList.add('is-in');
    en.target.querySelectorAll('[data-rb-count]').forEach(countUp);
    revealIO.unobserve(en.target);
  }), { threshold: .15 }) : null;

  const initReveal = (root = document) => {
    root.querySelectorAll('.rb-rv:not(.is-in)').forEach((el) => {
      if (revealIO && document.documentElement.classList.contains('rb-js')) revealIO.observe(el);
      else el.classList.add('is-in');
    });
    // Theme editor / no animation: still show final numbers formatted
    if (!document.documentElement.classList.contains('rb-js')) {
      root.querySelectorAll('[data-rb-count]').forEach((el) => {
        const n = parseFloat(el.dataset.rbCount);
        if (!isNaN(n)) el.textContent = (parseInt(el.dataset.rbDec, 10) || 0) ? n.toFixed(parseInt(el.dataset.rbDec, 10)) : n.toLocaleString('en-IN');
      });
    }
  };

  /* ---------- Slider arrows: scroll the visible rail in the same section ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rb-dir]');
    if (!btn) return;
    const scope = btn.closest('[data-rb-rails]');
    const rail = scope && [...scope.querySelectorAll('.rb-rail')].find((r) => !r.hidden);
    if (!rail) return;
    const first = rail.firstElementChild;
    const step = first ? first.getBoundingClientRect().width + 20 : 300;
    rail.scrollBy({ left: step * Number(btn.dataset.rbDir), behavior: 'smooth' });
  });

  /* ---------- Pill tabs ---------- */
  const selectTab = (scope, i) => {
    scope.querySelectorAll('[data-rb-tab]').forEach((t) => {
      const on = t.dataset.rbTab === String(i);
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });
    scope.querySelectorAll('[data-rb-panel]').forEach((p) => {
      const on = p.dataset.rbPanel === String(i);
      p.hidden = !on;
      if (on) { p.scrollLeft = 0; p.classList.add('is-in'); }
    });
  };
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-rb-tab]');
    if (tab) selectTab(tab.closest('[data-rb-tabs]'), tab.dataset.rbTab);
  });
  document.addEventListener('shopify:block:select', (e) => {
    if (e.target.matches && e.target.matches('[data-rb-tab]')) selectTab(e.target.closest('[data-rb-tabs]'), e.target.dataset.rbTab);
  });

  /* ---------- Hover-to-play videos (video slider + reviews) ---------- */
  const playIn = (box) => {
    const v = box && box.querySelector('video');
    if (!v) return;
    if (!v.getAttribute('src') && v.dataset.src) v.src = srcFor(v);
    const p = v.play();
    p && p.catch(() => {});
    box.classList.add('is-playing');
  };
  const pauseIn = (box) => {
    const v = box && box.querySelector('video');
    if (!v) return;
    v.pause();
    box.classList.remove('is-playing');
  };
  const hoverable = window.matchMedia('(hover: hover)');
  document.addEventListener('mouseover', (e) => {
    if (!hoverable.matches) return;
    const box = e.target.closest('.rb-vc, .rb-rc__media');
    if (!box || box.classList.contains('is-playing')) return;
    playIn(box);
    box.addEventListener('mouseleave', () => pauseIn(box), { once: true });
  });
  // Touch: tap a review video to play / pause
  document.addEventListener('click', (e) => {
    if (hoverable.matches) return;
    const box = e.target.closest('.rb-rc__media');
    if (!box) return;
    box.classList.contains('is-playing') ? pauseIn(box) : playIn(box);
  });
  // Touch: video-slider cards play while mostly in view
  const touchIO = !hoverable.matches && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((en) => (en.isIntersecting ? playIn(en.target) : pauseIn(en.target))), { threshold: .6 })
    : null;
  // Cards without a poster: fetch a preview frame only when they approach the viewport
  const previewIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => entries.forEach((en) => {
    if (!en.isIntersecting) return;
    const v = en.target;
    previewIO.unobserve(v);
    if (!v.getAttribute('src') && v.dataset.src) { v.preload = 'metadata'; v.src = v.dataset.src + '#t=1'; }
  }), { rootMargin: '300px 0px' }) : null;

  const initVideos = (root = document) => {
    if (previewIO) whenLoaded(() => root.querySelectorAll('video[data-rb-preview]').forEach((v) => previewIO.observe(v)));
    if (touchIO) root.querySelectorAll('.rb-vc').forEach((box) => box.querySelector('video') && touchIO.observe(box));
  };

  /* ---------- Quick add (one-variant products) → theme cart drawer ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form.rb-quick');
    if (!form || !document.querySelector('cart-drawer')) return; // no drawer: let the form post to /cart
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn && btn.setAttribute('aria-busy', 'true');
    try {
      const root = (window.theme && theme.routes && theme.routes.root_url) || '/';
      const res = await fetch(`${root.replace(/\/$/, '')}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(form.elements.id.value), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
      // Cart subscribers (cart drawer, cart page) expect the cart *with* their rendered sections
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      const cart = await (await fetch(`${root.replace(/\/$/, '')}/cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: {}, sections: [...new Set(sections)] }),
      })).json();
      try {
        if (window.theme && theme.pubsub) theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'rb-quick-add', cart });
      } catch (e) { console.error(e); }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    } catch (err) {
      window.alert(err.message);
    } finally {
      btn && btn.removeAttribute('aria-busy');
    }
  });

  /* ---------- Boot + theme editor re-renders ---------- */
  const boot = (root) => { initReveal(root); initVideos(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
